import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Clock, CheckCircle2, XCircle, Download, Package, Eye, AlertTriangle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { isFirebaseConfigured } from "@/lib/firebase";
import { listFirebaseOrders, listFirebaseUserOrders } from "@/lib/firebaseOrders";
import { getPaymentMethodLabel } from "@/lib/payments";
import { getCatalogApp } from "@/lib/catalog";
import { downloadApp } from "@/lib/downloads";
import InstallInstructions from "@/components/InstallInstructions";

const LOCAL_ORDERS_KEY = "weborder.localOrders";

function getLocalOrders() {
  try {
    return JSON.parse(localStorage.getItem(LOCAL_ORDERS_KEY) || "[]");
  } catch {
    return [];
  }
}

function getVisibleLocalOrders(user) {
  const localOrders = getLocalOrders();
  if (user?.role === "admin") return localOrders;
  if (!user?.user_id) return localOrders;
  return localOrders.filter((order) => !order.user_id || order.user_id === user.user_id);
}

function mergeOrders(primaryOrders, fallbackOrders) {
  const seen = new Set();
  return [...primaryOrders, ...fallbackOrders]
    .filter((order) => {
      const key = order.order_id;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
}

export default function Dashboard() {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloadingId, setDownloadingId] = useState(null);
  const [installApp, setInstallApp] = useState(null);

  useEffect(() => {
    if (isFirebaseConfigured && user?.user_id) {
      const loader = user.role === "admin" ? listFirebaseOrders : () => listFirebaseUserOrders(user.user_id);
      loader()
        .then((firebaseOrders) => {
          const localOrders = getVisibleLocalOrders(user);
          setOrders(mergeOrders(firebaseOrders, localOrders));
        })
        .catch(() => setOrders(getVisibleLocalOrders(user)))
        .finally(() => setLoading(false));
      return;
    }
    setOrders(getVisibleLocalOrders(user));
    setLoading(false);
  }, [user]);

  const handleDownload = async (appId) => {
    const app = getCatalogApp(appId) || { app_id: appId };
    setDownloadingId(appId);
    try {
      const started = await downloadApp(app);
      if (started) {
        toast.success("Download စတင်နေသည်… ပြီးနောက် install နည်းကို ကြည့်ပါ။");
        setInstallApp(app);
      } else {
        toast.error("Download လင့်ခ် မရနိုင်သေးပါ။ Admin စစ်ဆေးပေးပါမည်။ နောက်မှ ထပ်စမ်းကြည့်ပါ။");
      }
    } catch (err) {
      console.error("Download failed", err);
      toast.error("Download ဆွဲရန် အနည်းငယ် ချို့ယွင်းပါသည်။ ထပ်စမ်းကြည့်ပါ။");
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-6 py-12">
      <div>
        <div className="text-sm tracking-wide text-zinc-500 font-medium">အကောင့်</div>
        <h1 className="text-4xl sm:text-5xl font-bold tracking-tight">ဝယ်ယူမှုများ</h1>
        <p className="text-zinc-600 mt-2">အော်ဒါများ ခြေရာခံပြီး အတည်ပြုထားသော App များ Download ဆွဲပါ။</p>
      </div>

      <div className="mt-10">
        {loading ? (
          <div className="text-zinc-500">ဖွင့်နေသည်…</div>
        ) : orders.length === 0 ? (
          <div className="text-center bg-white border border-zinc-200 rounded-3xl p-16">
            <Package className="w-12 h-12 mx-auto text-zinc-300" />
            <h3 className="font-semibold text-xl mt-4">ဝယ်ယူမှု မရှိသေးပါ</h3>
            <p className="text-zinc-500 mt-2">ပထမဆုံး App ကို ဈေးကွက်တွင် ရှာဖွေပါ။</p>
            <Button asChild className="mt-6 bg-[#7CC576] hover:bg-[#5FB259] rounded-lg">
              <Link to="/apps">App များ ကြည့်ရန်</Link>
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {orders.map((o) => (
              <div
                key={o.order_id}
                data-testid={`order-row-${o.order_id}`}
                className="bg-white border border-zinc-200 rounded-2xl p-5 flex flex-col sm:flex-row sm:items-center gap-4"
              >
                <div className="flex items-center gap-4 min-w-0 flex-1">
                  <div className="flex-shrink-0">
                    <StatusBadge status={o.status} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-display font-semibold text-lg truncate">{o.app_title}</div>
                    <div className="text-xs text-zinc-500 mt-0.5 flex flex-wrap gap-x-3 gap-y-1">
                      <span className="break-all">အော်ဒါ {o.order_id}</span>
                      <span>·</span>
                      <span>{new Date(o.created_at).toLocaleString()}</span>
                      <span>·</span>
                      <span className="font-medium text-zinc-700 tabular-nums">{o.payment_method === "admin_gift" ? "လက်ဆောင်" : `${Number(o.amount_mmk || 0).toLocaleString()} ကျပ်`}</span>
                      <span>{getPaymentMethodLabel(o.payment_method)} ဖြင့်</span>
                    </div>
                  </div>
                </div>
                {o.status === "approved" && (
                  <div className="flex items-center gap-2 flex-shrink-0 flex-wrap">
                    <Button
                      data-testid={`download-${o.order_id}`}
                      onClick={() => handleDownload(o.app_id)}
                      disabled={downloadingId === o.app_id}
                      className="bg-[#7CC576] hover:bg-[#5FB259] rounded-lg"
                    >
                      {downloadingId === o.app_id
                        ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> ဆွဲနေသည်…</>
                        : <><Download className="w-4 h-4 mr-2" /> Download</>}
                    </Button>
                    {(getCatalogApp(o.app_id)?.platform === "android" || (o.app_id || "").includes("android")) && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setInstallApp(getCatalogApp(o.app_id) || { app_id: o.app_id, title: o.app_title })}
                        className="rounded-lg border-zinc-300"
                      >
                        ထည့်သွင်းနည်း
                      </Button>
                    )}
                  </div>
                )}
                {user?.role === "admin" && o.status === "pending" && (
                  <Button asChild data-testid={`dashboard-review-${o.order_id}`} className="bg-[#7CC576] hover:bg-[#5FB259] rounded-lg flex-shrink-0">
                    <Link to="/admin"><Eye className="w-4 h-4 mr-2" /> Payment စစ်ရန်</Link>
                  </Button>
                )}
              </div>
            ))}
            {orders.some((o) => o.status === "approved" && ((o.app_id || "").includes("android") || (getCatalogApp(o.app_id)?.platform === "android"))) && (
              <div className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
                <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>Note: Users will need to enable "Install from unknown sources" on their Android device to sideload the APK.  "ထည့်သွင်းနည်း" ကိုနှိပ်၍ အသေးစိတ်ကြည့်ပါ။</span>
              </div>
            )}
          </div>
        )}
      </div>

      <InstallInstructions
        open={Boolean(installApp)}
        onOpenChange={(v) => { if (!v) setInstallApp(null); }}
        app={installApp}
        onDownload={() => installApp && handleDownload(installApp.app_id)}
      />
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    pending:   { icon: Clock, label: "စောင့်ဆိုင်းနေသည်", cls: "bg-amber-50 text-amber-700 border-amber-200" },
    approved:  { icon: CheckCircle2, label: "အတည်ပြုပြီး", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
    rejected:  { icon: XCircle, label: "ငြင်းပယ်", cls: "bg-red-50 text-red-700 border-red-200" },
  };
  const s = map[status] || map.pending;
  const Icon = s.icon;
  return (
    <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-semibold uppercase tracking-[0.12em] ${s.cls}`}>
      <Icon className="w-3.5 h-3.5" /> {s.label}
    </div>
  );
}
